import { useEffect, useRef, useState } from "react";
import * as api from "../../api";

const num = n => String(Math.round(Number(n))).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
const CAMPOS = [["titulo", "Título"], ["estado", "Estado"], ["prioridad", "Prioridad"], ["fecha", "Fecha"], ["persona", "Persona"]];
const SIN = "";
const GRUPO_MODO = "fn-modo";
const PRIO_TXT = ["Baja", "Media", "Alta", "Crítica"];
const ESTADO_TXT = { abierta: "Abierta", en_curso: "En curso", bloqueada: "Bloqueada", cerrada: "Hecha" };
const diaCorto = f => {
  if (!f) return "—";
  const d = new Date(`${String(f).slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("es-ES", { weekday: "short", day: "numeric", month: "short" }).replace(".", "");
};

// Pantalla de mapeo y vista previa de una base elegida como «Mi lista de tareas» (T-141). Contrato: /integrations/notion/fuentes
// (T-140). Nada se guarda hasta «Guardar mi lista»; cambiar una columna o confirmar quien eres vuelve a pedir la vista previa.
// onGuardada(fuente): guardada. onCambiarBase(): volver al explorador. Si `fuente` viene, se edita esa lista (PATCH).
export default function FuenteNotion({ base, fuente = null, onGuardada, onCambiarBase, onCancelar }) {
  const [prev, setPrev] = useState(null);
  const [mapeo, setMapeo] = useState(fuente ? fuente.mapeo : null);   // null = el detectado
  const [yo, setYo] = useState(fuente ? fuente.yo : { ids: [], nombres: [] });
  const [modo, setModo] = useState(fuente ? fuente.modo : "manual");
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const detectado = useRef(null);
  const peticion = useRef(0);

  useEffect(() => {
    const n = ++peticion.current;
    setCargando(true);
    api.previaFuenteNotion({ database_id: base.id, ...(mapeo ? { mapeo } : {}), yo })
      .then(d => {
        if (n !== peticion.current) return;
        if (!detectado.current) detectado.current = d.mapeo;
        setPrev(d); setError(null);
      })
      .catch(e => { if (n === peticion.current) setError({ codigo: e?.code || "", mensaje: e?.message || "No se ha podido leer esa base" }); })
      .finally(() => { if (n === peticion.current) setCargando(false); });
  }, [base.id, mapeo, yo]);

  const m = prev ? prev.mapeo : null;
  const mias = prev ? prev.mias : null;
  const sinPersona = !!prev && !m.persona;
  const autoOk = !!mias && !sinPersona && (mias.criterio === "correo" || mias.criterio === "confirmado");
  const esManualDe = m_ => m_ !== "auto";
  const modoReal = modo === "auto" && !autoOk ? "manual" : modo;

  const esManual = esManualDe(modoReal);
  const claseModo = (marcado, disponible) => (marcado ? "on" : disponible ? "" : "off");
  const cambiar = (campo, valor) => setMapeo({ ...(m || {}), [campo]: valor === SIN ? null : valor });
  const confirmarSoy = () => { const r = mias.requiere_confirmacion; setYo({ ids: r.id ? [r.id] : [], nombres: r.id ? [] : [r.nombre] }); };
  const duda = campo => (prev?.dudas || []).find(d => d.campo === campo && d.motivo !== "sin_persona");

  const guardar = async () => {
    setGuardando(true); setError(null);
    try {
      const cuerpo = { mapeo: m, estados: prev.estados, yo, modo: modoReal };
      const f = fuente ? await api.cambiarFuenteNotion(fuente.id, cuerpo) : await api.guardarFuenteNotion({ database_id: base.id, ...cuerpo });
      onGuardada?.(f);
    } catch (e) {
      setError({ codigo: e?.code || "", mensaje: e?.message || "No se ha podido guardar la lista" });
      setGuardando(false);
    }
  };

  if (!prev && cargando) return <p className="mt-sel-nota" role="status">Leyendo «{base.titulo}»…</p>;
  if (!prev) {
    return (
      <div className="fn">
        <div className="ex-aviso ex-err" role="alert"><b>{error?.codigo === "NO_ENCONTRADO" ? "Esa base ya no está compartida con ALM." : "No se ha podido leer esa base."}</b> No se ha guardado nada.</div>
        <div className="mt-dlg-pie">
          {onCambiarBase && <button type="button" className="mt-b btn" onClick={onCambiarBase}>Elegir otra base</button>}
          <button type="button" className="mt-b p btn" onClick={() => setMapeo(mapeo ? { ...mapeo } : null)}>Reintentar</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fn">
      <div className="fn-cab">
        <b>{prev.base.titulo}</b>
        <small>Notion · {num(prev.base.filas)} filas</small>
        {onCambiarBase && !fuente && <button type="button" className="mt-b btn" onClick={onCambiarBase}>Cambiar de base</button>}
      </div>

      <div className="fn-map">
        {CAMPOS.map(([campo, etiqueta]) => {
          const d = duda(campo);
          const cols = prev.candidatos[campo] || [];
          const actual = m[campo] || SIN;
          const autom = detectado.current && detectado.current[campo] === m[campo] && !!m[campo];
          return (
            <div className="fn-campo" key={campo}>
              <label htmlFor={`fn-${campo}`}>{etiqueta}</label>
              <select id={`fn-${campo}`} value={actual} onChange={e => cambiar(campo, e.target.value)}>
                {campo !== "titulo" && <option value={SIN}>(Sin columna)</option>}
                {cols.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              {d ? <span className="fn-det duda">{d.motivo === "varias_fechas" ? `Elige una: hay ${d.candidatos.length} fechas` : `Hay ${d.candidatos.length} columnas de personas`}</span>
                 : campo === "persona" && sinPersona ? <span className="fn-det duda">No hay columna de persona</span>
                 : autom ? <span className="fn-det">detectada</span> : null}
            </div>
          );
        })}
      </div>

      {sinPersona ? (
        <div className="ex-aviso" role="status">Esta base no tiene columna de persona: no puedo saber cuáles son tuyas. Podrás <b>traerlas a mano</b>, una a una. El modo automático no está disponible aquí.</div>
      ) : mias.criterio === "nombre_sin_confirmar" && mias.requiere_confirmacion ? (
        <div className="fn-pregunta" role="group" aria-label="¿Eres tú?">
          <b>¿Eres «{mias.requiere_confirmacion.nombre}» en esta base?</b>
          <span>Notion no me da tu correo, solo el nombre. Si confirmas, serían <b>{num(mias.n_si_confirmas ?? 0)} tareas</b>.</span>
          <div className="fn-acc">
            <button type="button" className="mt-b p btn" onClick={confirmarSoy}>Sí, soy yo</button>
            <span className="fn-nota">Si no lo eres, no se trae nada tuyo: puedes traerlas a mano.</span>
          </div>
        </div>
      ) : mias.ambiguo ? (
        <div className="ex-aviso" role="status">Hay <b>más de una persona</b> con tu nombre y Notion no da el correo, así que no asumo cuál eres. Podrás <b>traerlas a mano</b>.</div>
      ) : (
        <p className="fn-cuenta" role="status"><b>{num(mias.n)} de {num(mias.total)}</b> filas son tuyas{prev.base.truncada ? " (se leen las primeras 5.000)" : ""}</p>
      )}

      {prev.base.truncada && <div className="ex-aviso" role="status"><b>Base muy grande.</b> Se leen las primeras 5.000 filas. Si faltan, usa una base más pequeña.</div>}
      {!sinPersona && mias.n === 0 && mias.criterio !== "nombre_sin_confirmar" && !mias.ambiguo && (
        <div className="ex-aviso" role="status"><b>Ninguna fila es tuya.</b> Revisa la columna de persona o elige otra base.</div>
      )}

      <div className="fn-tabla" aria-busy={cargando}>
        <table className="fn-prev">
          <caption className="fn-sr">Vista previa de {prev.muestra.length} filas</caption>
          <thead><tr><th>Título</th><th>Estado</th><th>Prioridad</th><th>Fecha</th><th>Persona</th></tr></thead>
          <tbody>
            {prev.muestra.map(f => (
              <tr key={f.id} className={f.mia ? "mia" : ""}>
                <td><b>{f.titulo}</b>{f.mia && <span className="fn-det"> tuya</span>}</td>
                <td>{ESTADO_TXT[f.estado] || "—"}</td>
                <td>{PRIO_TXT[f.prioridad] || "—"}</td>
                <td>{diaCorto(f.fecha)}</td>
                <td>{f.personas.length ? f.personas.join(", ") : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="fn-nota">Cambia una columna y la vista previa se actualiza. Las filas tuyas salen primero.</p>

      <fieldset className="fn-modo">
        <legend>¿Cómo quieres traer las tareas?</legend>
        <label className={claseModo(esManual, true)}>
          <input type="radio" name={GRUPO_MODO} checked={esManual} onChange={() => setModo("manual")} />
          <span><b>Manual (recomendado)</b><small>Te aviso de las nuevas y tú las añades con «Añadir las N nuevas».</small></span>
        </label>
        <label className={claseModo(!esManual, autoOk)}>
          <input type="radio" name={GRUPO_MODO} checked={!esManual} disabled={!autoOk} onChange={() => setModo("auto")} />
          <span><b>Automático{autoOk ? "" : " (no disponible)"}</b>
            <small>{autoOk ? "Las tuyas se añaden solas. Úsalo cuando ya confíes en las columnas." : sinPersona ? "Necesita una columna de persona." : "Necesita saber con certeza cuáles son tuyas."}</small></span>
        </label>
      </fieldset>

      {error && <div className="ex-aviso ex-err" role="alert"><b>{error.codigo === "MIGRACION" ? "Esta función aún no está activada en el servidor." : error.mensaje}</b> No se ha guardado nada.</div>}
      <div className="mt-dlg-pie">
        <button type="button" className="mt-b btn" onClick={onCancelar}>Cancelar</button>
        <button type="button" className="mt-b p btn" disabled={guardando || cargando} onClick={guardar}>{guardando ? "Guardando…" : fuente ? "Guardar cambios" : "Guardar mi lista"}</button>
      </div>
    </div>
  );
}
